if (!customElements.get('monoree-new-arrivals')) {
  customElements.define('monoree-new-arrivals', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.selection === 'complete' || this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.querySelector('[data-new-arrivals-retry]')?.addEventListener('click', () => this.load(), { signal: this.lifecycle.signal });
      this.load();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
      this.lifecycle = null;
      this.request = null;
    }

    cardsFrom(section) {
      const source = section.querySelector('[data-new-arrivals-candidates]')?.content || section.querySelector('[data-new-arrivals-grid]');
      const cards = [...source.querySelectorAll('[data-new-product-id]')];
      if (cards.some(card => !card.dataset.newProductId || !Number.isFinite(Number(card.dataset.publishedAt)))) {
        throw new Error('Invalid new arrival');
      }
      return cards;
    }

    async page(page, signal) {
      for (let attempt = 0; attempt < 2; attempt++) {
        const controller = new AbortController();
        const cancel = () => controller.abort();
        signal.addEventListener('abort', cancel, { once: true });
        const timer = setTimeout(cancel, 8000);
        try {
          if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
          const url = new URL(location.href);
          url.hash = '';
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set(this.dataset.pageParam, String(page));
          const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('New arrivals page unavailable');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = [...html.querySelectorAll('monoree-new-arrivals')].find(node => node.dataset.sectionId === this.dataset.sectionId);
          if (!section || Number(section.dataset.currentPage) !== page || section.dataset.pages !== this.dataset.pages || section.dataset.catalogCount !== this.dataset.catalogCount || (section.dataset.displayMode || 'home') !== (this.dataset.displayMode || 'home')) {
            throw new Error('New arrivals page incomplete');
          }
          return section;
        } catch (error) {
          if (signal.aborted || attempt === 1) throw error;
        } finally {
          clearTimeout(timer);
          signal.removeEventListener('abort', cancel);
        }
      }
    }

    async load() {
      if (this.dataset.selection === 'complete' || this.request) return;
      const controller = new AbortController();
      this.request = controller;
      const grid = this.querySelector('[data-new-arrivals-grid]');
      const status = this.querySelector('[data-new-arrivals-status]');
      const message = this.querySelector('[data-new-arrivals-message]');
      const retry = this.querySelector('[data-new-arrivals-retry]');
      this.dataset.selection = 'loading';
      this.setAttribute('aria-busy', 'true');
      message.textContent = message.dataset.loading;
      retry.hidden = true;
      retry.setAttribute('aria-disabled', 'true');
      try {
        const pages = Number(this.dataset.pages);
        const currentPage = Number(this.dataset.currentPage);
        const catalogCount = Number(this.dataset.catalogCount);
        const catalogMode = this.dataset.displayMode === 'catalog';
        if (!Number.isInteger(pages) || pages < 1 || !Number.isInteger(catalogCount) || catalogCount < 0) throw new Error('Invalid catalog');
        const candidates = new Map(this.cardsFrom(this).map(card => [card.dataset.newProductId, card]));
        let covered = Number(this.dataset.pageSourceCount);
        for (let page = 1; page <= pages; page++) {
          if (page === currentPage) continue;
          const section = await this.page(page, controller.signal);
          if (section.dataset.selection === 'complete') {
            candidates.clear();
            this.cardsFrom(section).forEach(card => candidates.set(card.dataset.newProductId, card));
            covered = catalogCount;
            break;
          }
          covered += Number(section.dataset.pageSourceCount);
          this.cardsFrom(section).forEach(card => candidates.set(card.dataset.newProductId, card));
        }
        if (covered !== catalogCount) throw new Error('Incomplete catalog coverage');
        const sorted = [...candidates.values()].sort((a, b) => Number(b.dataset.publishedAt) - Number(a.dataset.publishedAt) || b.dataset.newProductId.localeCompare(a.dataset.newProductId, undefined, { numeric: true }));
        const winners = catalogMode ? sorted : sorted.slice(0, 4);
        if (!catalogMode && winners.length !== Math.min(4, catalogCount)) throw new Error('Incomplete selection');
        if (controller.signal.aborted || !this.isConnected) return;
        if (grid.children.length) throw new Error('Selection already visible');
        grid.replaceChildren(...winners.map(card => document.importNode(card, true)));
        grid.hidden = catalogMode && winners.length === 0;
        const empty = this.querySelector('[data-new-arrivals-empty]');
        if (catalogMode && empty) empty.hidden = winners.length > 0;
        const moveFocus = status.contains(document.activeElement);
        status.hidden = true;
        this.querySelector('[data-new-arrivals-candidates]')?.remove();
        this.dataset.selection = 'complete';
        if (moveFocus) (grid.querySelector('a') || this.querySelector('[data-new-arrivals-catalog-link]'))?.focus({ preventScroll: true });
      } catch (error) {
        if (!controller.signal.aborted) {
          this.dataset.selection = 'error';
          message.textContent = message.dataset.error;
          retry.hidden = false;
        }
      } finally {
        if (this.request === controller) {
          this.request = null;
          this.removeAttribute('aria-busy');
          retry.removeAttribute('aria-disabled');
        }
      }
    }
  });
}
